import React, { lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { StudioProvider, useStudio } from "@/store/StudioContext";
import { AuthProvider, useAuth } from "@/store/AuthContext";
import AppShell from "@/components/layout/AppShell";
import HomePage from "@/pages/HomePage";
import OnboardingPage from "@/pages/OnboardingPage";
import SetupPage from "@/pages/SetupPage";
import LoginPage from "@/pages/LoginPage";
import RecoveryPage from "@/pages/RecoveryPage";
import InviteAcceptPage from "@/pages/InviteAcceptPage";
import ProjectsPage from "@/pages/ProjectsPage";
import ProjectWorkspace from "@/pages/ProjectWorkspace";
import CharactersPage from "@/pages/CharactersPage";
import GalleryPage from "@/pages/GalleryPage";
import TasksPage from "@/pages/TasksPage";
import SettingsPage from "@/pages/SettingsPage";
import QuickToolPage from "@/pages/QuickToolPage";
import AdminPage, { isUserOwner } from "@/pages/AdminPage";
import NotFoundPage from "@/pages/NotFoundPage";

const IsolatedProposalPage =
  process.env.NODE_ENV !== "production"
    ? lazy(() => import("@/pages/IsolatedProposalPage"))
    : null;

const AdminRoute = ({ children }) => {
  const { user } = useAuth();
  if (!isUserOwner(user)) {
    return <Navigate to="/" replace />;
  }
  return children;
};

const OnboardingGate = ({ children }) => {
  const { state } = useStudio();
  const location = useLocation();
  if (state.onboarding.status === "not_started" && location.pathname !== "/onboarding") return <Navigate to="/onboarding" replace />;
  return children;
};

// Acceso privado de un único propietario (Fase 2): /setup y /login quedan
// siempre accesibles; todo lo demás exige sesión. `authLoading` evita un
// parpadeo hacia /login mientras se confirma si ya hay cookie válida.
const AuthGate = ({ children }) => {
  const { user, authLoading, setupRequired } = useAuth();
  const location = useLocation();
  const path = location.pathname;

  if (authLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#101010]" data-testid="auth-loading">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#303030] border-t-[#f5b544]" />
      </div>
    );
  }
  if (setupRequired && path !== "/setup" && path !== "/login" && path !== "/invite") return <Navigate to="/setup" replace />;
  if (!user && path !== "/login" && path !== "/setup" && path !== "/recovery" && path !== "/invite") return <Navigate to="/login" replace />;
  if (user && (path === "/login" || path === "/setup" || path === "/recovery" || path === "/invite")) return <Navigate to="/" replace />;
  return children;
};

const StudioGate = () => {
  const { user } = useAuth();
  if (!user) return null;
  return (
    <StudioProvider key={user?.id || user?._id || user?.email || "auth-user"}>
      <OnboardingGate>
        <Routes>
          <Route path="/onboarding" element={<OnboardingPage />} />
          <Route element={<AppShell />}>
            <Route index element={<HomePage />} />
            <Route path="/projects" element={<ProjectsPage />} />
            <Route path="/projects/:projectId" element={<Navigate to="storyboard" replace />} />
            <Route path="/projects/:projectId/:view" element={<ProjectWorkspace />} />
            <Route path="/characters" element={<CharactersPage />} />
            <Route path="/gallery" element={<GalleryPage />} />
            <Route path="/tasks" element={<TasksPage />} />
            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <AdminPage />
                </AdminRoute>
              }
            />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/tools/:tool" element={<QuickToolPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </OnboardingGate>
    </StudioProvider>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter basename={process.env.PUBLIC_URL || ""}>
        <Routes>
          {process.env.NODE_ENV !== "production" && IsolatedProposalPage && (
            <Route
              path="/test-proposal"
              element={
                <Suspense fallback={null}>
                  <IsolatedProposalPage />
                </Suspense>
              }
            />
          )}
          <Route
            path="/*"
            element={
              <AuthGate>
                <Routes>
                  <Route path="/setup" element={<SetupPage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/recovery" element={<RecoveryPage />} />
                  <Route path="/invite" element={<InviteAcceptPage />} />
                  <Route path="/*" element={<StudioGate />} />
                </Routes>
              </AuthGate>
            }
          />
        </Routes>
      </BrowserRouter>
      <Toaster theme="dark" position="bottom-right" toastOptions={{ style: { background: "#1b1b1b", border: "1px solid #303030", color: "#f4f1ec" } }} />
    </AuthProvider>
  );
}

export default App;

